import { useMemo, useState, type ChangeEvent, type KeyboardEvent } from "react";
import type { CheckedState } from "@radix-ui/react-checkbox";

import { GripVertical } from "lucide-react";
import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import CapabilityGate from "@/components/CapabilityGate";
import LockedFeatureCard from "@/components/pricing/LockedFeatureCard";
import { useCapabilities } from "@/hooks/useCapabilities";
import { compactCityLabel } from "@/lib/geography";
import type { RoutingQueryInput } from "@/types/routing";

const STATE_OPTIONS = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DC",
  "DE",
  "FL",
  "GA",
  "IA",
  "ID",
  "IL",
  "IN",
  "KS",
  "KY",
  "LA",
  "MA",
  "MD",
  "ME",
  "MI",
  "MN",
  "MO",
  "MS",
  "MT",
  "NC",
  "ND",
  "NE",
  "NH",
  "NJ",
  "NM",
  "NV",
  "NY",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VA",
  "VT",
  "WA",
  "WI",
  "WV",
  "WY",
];

const STATE_SET = new Set(STATE_OPTIONS);

function normalizeState(value: string) {
  const trimmed = value.trim().toUpperCase();
  return STATE_SET.has(trimmed) ? trimmed : "";
}

function parsePriorityLabel(label: string): { city: string; state: string } {
  if (!label) return { city: "", state: "" };
  const [cityPart, ...stateParts] = label.split(",");
  return {
    city: cityPart?.trim() ?? "",
    state: stateParts.join(",").trim(),
  };
}

function buildPriorityLabel(city: string, state: string): string {
  return compactCityLabel(city, state);
}

type RoutingControlsProps = {
  query: RoutingQueryInput;
  onQueryChange: (partial: Partial<RoutingQueryInput>, autosuggest?: boolean) => void;
  onSubmit: () => void;
  loading: boolean;
};

export function RoutingControls({ query, onQueryChange, onSubmit, loading }: RoutingControlsProps) {
  const { capabilities, openUpgradeModal } = useCapabilities();
  const stateValue = useMemo(() => query.states ?? [], [query.states]);
  const priorityValues = useMemo(() => query.priority_cities ?? [], [query.priority_cities]);
  const [newStopInput, setNewStopInput] = useState("");
  const startState = query.start_state ?? "";
  const endState = query.end_state ?? "";
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const handleStateToggle = (code: string) => (checked: CheckedState) => {
    const nextStates =
      checked === true ? Array.from(new Set([...stateValue, code])) : stateValue.filter((value) => value !== code);
    onQueryChange({ states: nextStates }, true);
  };

  const handleSelectAllStates = (checked: CheckedState) => {
    if (checked === true) {
      onQueryChange({ states: [...STATE_OPTIONS] }, true);
    } else {
      onQueryChange({ states: [] }, true);
    }
  };

  const handleSliderChange = (field: keyof RoutingQueryInput) => (event: ChangeEvent<HTMLInputElement>) => {
    onQueryChange({ [field]: Number(event.target.value) }, true);
  };

  const parseFutureStopInput = (input: string) => {
    const [cityPart, ...stateParts] = input.split(",");
    return {
      city: cityPart?.trim() ?? "",
      state: stateParts.join(",").trim(),
    };
  };

  const handleAddStopFromInput = () => {
    const trimmed = newStopInput.trim();
    if (!trimmed) return;
    const { city, state } = parseFutureStopInput(trimmed);
    const next = [...stopItems];
    next.splice(Math.max(next.length - 1, 0), 0, { id: `stop-${next.length}`, city, state });
    applyStopOrder(next);
    setNewStopInput("");
  };

  const allStatesSelected = stateValue.length === STATE_OPTIONS.length;
  const masterStateChecked: CheckedState = allStatesSelected ? true : stateValue.length ? "indeterminate" : false;

  const stopItems = useMemo<StopItem[]>(() => {
    const start = { city: query.start_city ?? "", state: startState };
    const end = { city: query.end_city ?? "", state: endState };
    const middle = priorityValues.map(parsePriorityLabel);
    const raw = [start, ...middle, end];
    if (raw.length < 2) {
      raw.push({ city: "", state: "" });
    }
    return raw.map((item, index) => ({ id: `stop-${index}`, ...item }));
  }, [endState, priorityValues, query.end_city, query.start_city, startState]);

  const applyStopOrder = (items: StopItem[]) => {
    const normalized = items.map((item) => ({
      city: item.city ?? "",
      state: normalizeState(item.state ?? ""),
    }));
    const start = normalized[0] ?? { city: "", state: "" };
    const end = normalized.length > 1 ? normalized[normalized.length - 1] : { city: "", state: "" };
    const middle = normalized.slice(1, -1);
    const derivedStates = Array.from(new Set(normalized.map((item) => item.state).filter(Boolean)));
    const mergedStates = Array.from(new Set([...stateValue, ...derivedStates]));
    onQueryChange(
      {
        start_city: start.city,
        start_state: start.state || null,
        end_city: end.city || null,
        end_state: end.state || null,
        priority_cities: middle.map((item) => buildPriorityLabel(item.city, item.state)),
        states: mergedStates,
      },
      true,
    );
  };

  const handleStopDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const current = [...stopItems];
    const oldIndex = current.findIndex((item) => item.id === active.id);
    const newIndex = current.findIndex((item) => item.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    current.splice(newIndex, 0, current.splice(oldIndex, 1)[0]);
    applyStopOrder(current);
  };

  const handleStopFieldChange =
    (index: number, field: "city" | "state") =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = event.target.value;
      const next = stopItems.map((item, idx) => (idx === index ? { ...item, [field]: value } : item));
      applyStopOrder(next);
    };

  const handleAddBlankStop = () => {
    const next = [...stopItems];
    next.splice(Math.max(next.length - 1, 0), 0, { id: `stop-${next.length}`, city: "", state: "" });
    applyStopOrder(next);
  };

  const handleRemoveStop = (index: number) => {
    if (stopItems.length <= 2) return;
    const next = stopItems.filter((_, idx) => idx !== index);
    applyStopOrder(next);
  };

  return (
    <Card className="aa-glass-card rounded-2xl">
      <CardHeader className="space-y-1 px-6 pt-6 pb-4">
        <CardTitle className="text-lg font-semibold tracking-wide text-foreground/90">Routing controls</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 px-6 pb-6 text-sm text-foreground/80">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/40 bg-transparent p-3 text-xs text-muted-foreground">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">States & regions</p>
            <p className="text-xs text-muted-foreground">Auto-filled from your stops. Edit if needed.</p>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline">
                {stateValue.length ? `${stateValue.length} selected` : "Select states"}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 space-y-3 p-3">
              <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground">
                <span>States</span>
                <label className="flex items-center gap-2 text-[11px] font-medium text-muted-foreground">
                  <Checkbox checked={masterStateChecked} onCheckedChange={handleSelectAllStates} />
                  <span>{allStatesSelected ? "Deselect all" : "Select all"}</span>
                </label>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {STATE_OPTIONS.map((code) => (
                  <label
                    key={code}
                    className="flex items-center gap-3 rounded-lg px-2 py-1 text-sm text-foreground transition hover:bg-background/5"
                  >
                    <Checkbox checked={stateValue.includes(code)} onCheckedChange={handleStateToggle(code)} />
                    <span>{code}</span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-3 rounded-2xl border border-border/40 bg-transparent p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Route anchors</p>
              <p className="text-xs text-muted-foreground">
                Drag to reorder. Top becomes Start, bottom becomes End.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={handleAddBlankStop}>
              Add blank stop
            </Button>
          </div>
          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              handleAddStopFromInput();
            }}
          >
            <input
              type="text"
              value={newStopInput}
              onChange={(event) => setNewStopInput(event.target.value)}
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAddStopFromInput();
                }
              }}
              placeholder="Add city, state (optional)"
              className="w-full rounded-xl border border-border bg-transparent px-3 py-2 text-sm"
            />
            <Button size="sm" type="submit">
              Add
            </Button>
          </form>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleStopDragEnd}>
            <SortableContext items={stopItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {stopItems.map((item, index) => (
                  <SortableStopRow
                    key={item.id}
                    id={item.id}
                    index={index}
                    total={stopItems.length}
                    city={item.city}
                    state={item.state}
                    onCityChange={handleStopFieldChange(index, "city")}
                    onStateChange={handleStopFieldChange(index, "state")}
                    onRemove={() => handleRemoveStop(index)}
                    canRemove={stopItems.length > 2}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <p className="text-xs text-muted-foreground">
            Add at least two stops to bookend the tour. Start and End can be the same city for round trips.
          </p>
        </div>
        <div className="rounded-2xl border border-border/5 bg-[#10131c]/70 p-4 space-y-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Target window</p>
          <label className="block text-xs uppercase tracking-wide text-muted-foreground">
            Start date
            <input
              type="date"
              className="mt-2 w-full rounded-xl border border-border bg-transparent px-3 py-2"
              value={query.start_date ?? ""}
              onChange={(event) => onQueryChange({ start_date: event.target.value || null }, true)}
            />
          </label>
          <label className="block text-xs uppercase tracking-wide text-muted-foreground">
            End date
            <input
              type="date"
              className="mt-2 w-full rounded-xl border border-border bg-transparent px-3 py-2"
              value={query.end_date ?? ""}
              onChange={(event) => onQueryChange({ end_date: event.target.value || null }, true)}
            />
          </label>
        </div>
        <CapabilityGate
          flag="canUseRoutingAI"
          fallback={
            <LockedFeatureCard
              title="Drive-time guardrails"
              description="Upgrade to set maximum daily drive hours."
              variant="inline"
              onUpgrade={() => openUpgradeModal("AI routing")}
            />
          }
        >
          <div className="space-y-3">
            <label className="block text-xs uppercase tracking-wide text-muted-foreground">
              Max drive hours: {query.max_drive_hours?.toFixed(1)}
            </label>
            <input
              type="range"
              min={3}
              max={12}
              step={0.5}
              value={query.max_drive_hours ?? 6}
              onChange={handleSliderChange("max_drive_hours")}
              className="w-full"
            />
          </div>
        </CapabilityGate>
        <CapabilityGate
          flag="canUseRoutingAI"
          fallback={
            <LockedFeatureCard
              title="Show spacing optimization"
              description="Pro routes balance show cadence automatically."
              variant="inline"
              onUpgrade={() => openUpgradeModal("AI routing")}
            />
          }
        >
          <div className="space-y-3">
            <label className="block text-xs uppercase tracking-wide text-muted-foreground">
              Show spacing (days): {query.target_show_frequency_days ?? 2}
            </label>
            <input
              type="range"
              min={1}
              max={5}
              step={1}
              value={query.target_show_frequency_days ?? 2}
              onChange={handleSliderChange("target_show_frequency_days")}
              className="w-full"
            />
          </div>
        </CapabilityGate>
        <CapabilityGate
          flag="canUseRoutingAI"
          fallback={
            <Button className="w-full" onClick={() => openUpgradeModal("AI routing")}>
              Unlock AI routing
            </Button>
          }
        >
          <Button className="w-full" onClick={onSubmit} disabled={loading}>
            {loading ? "Generating…" : "Suggest route"}
          </Button>
        </CapabilityGate>
      </CardContent>
    </Card>
  );
}

type StopItem = {
  id: string;
  city: string;
  state: string;
};

type SortableStopRowProps = {
  id: string;
  index: number;
  total: number;
  city: string;
  state: string;
  onCityChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onStateChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  onRemove: () => void;
  canRemove: boolean;
};

function SortableStopRow({
  id,
  index,
  total,
  city,
  state,
  onCityChange,
  onStateChange,
  onRemove,
  canRemove,
}: SortableStopRowProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const positionLabel = index === 0 ? "Start" : index === total - 1 ? "End" : `Stop ${index + 1}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex flex-col gap-2 rounded-xl border border-border/60 bg-transparent p-3 sm:flex-row sm:items-end ${
        isDragging ? "shadow-lg shadow-primary/20 ring-2 ring-primary/40" : ""
      }`}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        className="flex items-center gap-2 text-xs text-muted-foreground"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
        <span className="rounded-full border border-border/50 px-2 py-0.5 text-[11px] uppercase tracking-wide text-foreground/70">
          {positionLabel}
        </span>
      </button>
      <label className="flex-1 text-[11px] uppercase tracking-wide text-muted-foreground">
        City
        <input
          type="text"
          value={city}
          onChange={onCityChange}
          placeholder="Denver"
          className="mt-2 w-full rounded-xl border border-border bg-transparent px-3 py-2 text-sm"
        />
      </label>
      <label className="text-[11px] uppercase tracking-wide text-muted-foreground sm:w-28">
        State
        <select
          value={state}
          onChange={onStateChange}
          className="mt-2 w-full rounded-xl border border-border bg-transparent px-3 py-2 text-sm text-foreground/90"
        >
          <option value="">Any</option>
          {STATE_OPTIONS.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
      </label>
      <Button variant="ghost" size="sm" onClick={onRemove} disabled={!canRemove}>
        Remove
      </Button>
    </div>
  );
}
